import { useEffect, useRef } from "react";
import { UserRole } from "../../../app/userSlice";

interface UseTopLevelOrgSyncArgs {
	role: UserRole | null;
	topLevelDistrictId?: number | null;
	topLevelSchoolId?: number | null;
	setDistrictFilter: (value: number) => void;
	setSchoolFilter: (value: number) => void;
}

// Sync report filters to the top-level district/school picker (DistrictPicker/SchoolPicker)
// whenever the DistrictAdmin/SchoolAdmin switches their org context, so that the reports
// default to whatever the user just switched to at the top level.
export function useTopLevelOrgSync({
	role,
	topLevelDistrictId,
	topLevelSchoolId,
	setDistrictFilter,
	setSchoolFilter,
}: UseTopLevelOrgSyncArgs) {
	// Initialize the refs with the current top-level values (rather than an
	// "unset" sentinel) so that on the very first render, previous === current
	// and no sync fires - initial defaults are handled by the existing URL
	// param / "selected" flag logic elsewhere. This avoids needing any special
	// "initial run" case below.
	const previousDistrictIdRef = useRef<number | null>(topLevelDistrictId ?? null);
	const previousSchoolIdRef = useRef<number | null>(topLevelSchoolId ?? null);

	useEffect(() => {
		if (role !== UserRole.DistrictAdmin && role !== UserRole.SchoolAdmin) return;

		const currentDistrictId = topLevelDistrictId ?? null;
		const currentSchoolId = topLevelSchoolId ?? null;

		const previousDistrictId = previousDistrictIdRef.current;
		const previousSchoolId = previousSchoolIdRef.current;

		const districtChanged = previousDistrictId !== currentDistrictId;
		const schoolChanged = previousSchoolId !== currentSchoolId;

		previousDistrictIdRef.current = currentDistrictId;
		previousSchoolIdRef.current = currentSchoolId;

		if (role === UserRole.DistrictAdmin && districtChanged) {
			setDistrictFilter(currentDistrictId ?? 0);
		}

		if (role === UserRole.SchoolAdmin && schoolChanged) {
			if (districtChanged) {
				setDistrictFilter(currentDistrictId ?? 0);
			}
			setSchoolFilter(currentSchoolId ?? 0);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [role, topLevelDistrictId, topLevelSchoolId]);
}
